import { useAuthStore } from '@/stores/useAuthStore'

interface CoreRefreshState {
  leaderId: string | null
  leaderHeartbeat: number
  expiresAt?: number
  nextRefreshAt?: number
  heartbeat: number
  refreshCount?: number
}

const CORE_STATE_KEY = 'coreRefreshState'
const LOGOUT_SENTINEL_KEY = 'core_auth_logout'
const LOGIN_SENTINEL_KEY = 'core_auth_login'
const HEARTBEAT_EVERY_MS = 10_000
const HEARTBEAT_STALE_MS = 25_000
const MIN_LEAD_TIME_MS = 120_000
const MIN_DELAY_MS = 30_000

let tabId = ''
let isLeader = false
let heartbeatTimer: number | undefined
let refreshTimer: number | undefined
let escalateFn: (() => Promise<boolean>) | null = null

function now() {
  return Date.now()
}

function readState(): CoreRefreshState {
  const raw = localStorage.getItem(CORE_STATE_KEY)
  return raw
    ? (JSON.parse(raw) as CoreRefreshState)
    : { leaderId: null, leaderHeartbeat: 0, heartbeat: 0, refreshCount: 0 }
}

function writeState(update: Partial<CoreRefreshState>) {
  const cur = readState()
  const next: CoreRefreshState = { ...cur, ...update, heartbeat: (cur.heartbeat ?? 0) + 1 }
  localStorage.setItem(CORE_STATE_KEY, JSON.stringify(next))
  return next
}

function startHeartbeat() {
  stopHeartbeat()
  heartbeatTimer = window.setInterval(() => {
    writeState({ leaderId: tabId, leaderHeartbeat: now() })
  }, HEARTBEAT_EVERY_MS) as unknown as number
}

function stopHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer)
  heartbeatTimer = undefined
}

function clearRefreshTimer() {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = undefined
}

function scheduleRefreshAt(nextRefreshAt: number) {
  clearRefreshTimer()
  const delay = Math.max(1_000, nextRefreshAt - now())
  refreshTimer = window.setTimeout(async () => {
    const state = readState()
    if (state.leaderId !== tabId) return

    const authStore = useAuthStore()
    const ok = await navigator.locks.request('boost-core-refresh', () => authStore.coreRefresh())
    if (ok) {
      writeState({ refreshCount: (state.refreshCount ?? 0) + 1 })
      const refreshed = readState()
      const abs = refreshed.expiresAt ?? state.expiresAt
      if (abs) publishSchedule(abs)
      return
    }

    // First retry after 5 s
    await new Promise((r) => setTimeout(r, 5_000))
    const retryState = readState()
    if (retryState.leaderId !== tabId) return

    const retryOk = await navigator.locks.request('boost-core-refresh', () => authStore.coreRefresh())
    if (retryOk) {
      writeState({ refreshCount: (retryState.refreshCount ?? 0) + 1 })
      const abs = readState().expiresAt ?? retryState.expiresAt
      if (abs) publishSchedule(abs)
      return
    }

    // Escalate: full OIDC refresh → new core session
    if (escalateFn) {
      const recovered = await escalateFn()
      if (recovered) {
        const abs = readState().expiresAt
        if (abs) publishSchedule(abs)
      }
    }
  }, delay) as unknown as number
}

function publishSchedule(expiresAt: number) {
  const plannedAt = Math.max(now() + MIN_DELAY_MS, expiresAt - MIN_LEAD_TIME_MS)
  const state = writeState({ expiresAt, nextRefreshAt: plannedAt })
  if (isLeader && state.nextRefreshAt) {
    scheduleRefreshAt(state.nextRefreshAt)
  }
}

function becomeLeader() {
  if (isLeader) return
  isLeader = true
  startHeartbeat()
  const state = readState()
  if (state.nextRefreshAt) scheduleRefreshAt(state.nextRefreshAt)
}

function resignLeadership() {
  if (!isLeader) return
  isLeader = false
  stopHeartbeat()
  clearRefreshTimer()
}

function tryBecomeLeader() {
  const cur = readState()
  const leaderIsStale = !cur.leaderId || now() - (cur.leaderHeartbeat || 0) > HEARTBEAT_STALE_MS
  if (!leaderIsStale) return false
  writeState({ leaderId: tabId, leaderHeartbeat: now() })
  const check = readState()
  if (check.leaderId === tabId) {
    becomeLeader()
    return true
  }
  return false
}

function onStorage(ev: StorageEvent) {
  if (ev.key !== CORE_STATE_KEY || !ev.newValue) return
  const state = JSON.parse(ev.newValue) as CoreRefreshState
  const leaderIsStale = !state.leaderId || now() - (state.leaderHeartbeat || 0) > HEARTBEAT_STALE_MS

  if (!isLeader && leaderIsStale) {
    tryBecomeLeader()
  }
  if (isLeader && state.leaderId !== tabId) {
    resignLeadership()
  }
  if (isLeader && state.nextRefreshAt) {
    scheduleRefreshAt(state.nextRefreshAt)
  }
}

/**
 * @param onEscalate Called when both coreRefresh attempts fail.
 *   Should perform a full OIDC silent renew + coreLogin.
 *   Pass `refreshBoostCoreSession` from oidcAuth.ts.
 */
export function initCoreSessionHandler(onEscalate?: () => Promise<boolean>) {
  tabId = `${now()}-${Math.random().toString(16).slice(2)}`
  escalateFn = onEscalate ?? null
  window.addEventListener('storage', onStorage)
  tryBecomeLeader()
}

export function publishExpiry(expiresAt?: number) {
  if (!expiresAt) return
  publishSchedule(expiresAt)
}

export function stopCoreSessionHandler() {
  window.removeEventListener('storage', onStorage)
  resignLeadership()
}

/**
 * Notifies other tabs that the user has logged out.
 * Writes a sentinel into localStorage; the value is ignored, only the change matters.
 */
export function broadcastLogout() {
  try {
    localStorage.setItem(LOGOUT_SENTINEL_KEY, String(Date.now()))
  } catch { /* ignore */ }
}

/**
 * Notifies other tabs that a fresh authentication has just been established.
 * Stale /login tabs listen for this and auto-navigate to their saved per-tab URL.
 */
export function broadcastLogin() {
  try {
    localStorage.setItem(LOGIN_SENTINEL_KEY, String(Date.now()))
  } catch { /* ignore */ }
}

const LOGOUT_HANDLED_AT_KEY = 'logout_broadcast_handled_at'

/**
 * Listens for logout broadcasts from other tabs.
 * Must be initialized once at app startup, regardless of auth method.
 *
 * Anti-flood: each tab acts at most once per ~10 s. A tab that already handled
 * a broadcast (and is now navigating) ignores subsequent sentinels.
 */
export function initLogoutBroadcast() {
  window.addEventListener('storage', async (ev: StorageEvent) => {
    if (ev.key !== LOGOUT_SENTINEL_KEY || !ev.newValue) return

    const lastHandled = Number(sessionStorage.getItem(LOGOUT_HANDLED_AT_KEY) ?? 0)
    if (Date.now() - lastHandled < 10_000) return

    const path = window.location.pathname
    if (
      path.startsWith('/auth/login') ||
      path.startsWith('/auth/logout') ||
      path.startsWith('/login') ||
      path.startsWith('/disconnected')
    ) return

    sessionStorage.setItem(LOGOUT_HANDLED_AT_KEY, String(Date.now()))
    resignLeadership()
    const { gotoLogin } = await import('@/auth/gotoLogin')
    gotoLogin()
  })
}
